import type {
  CategoryKind,
  PublicMenuItem,
  UpsellRule,
  UpsellShowIn,
  UpsellSuggestion,
  WizardStep,
} from '@/types/menu-sales';

type CategoryRef = { id: string; name: string; kind: CategoryKind };

function normalizeKeywords(value: string[]): string[] {
  return value.map((k) => k.trim().toLowerCase()).filter(Boolean);
}

function haystackForItem(item: PublicMenuItem, categoryName?: string): string {
  return `${item.name} ${item.description ?? ''} ${categoryName ?? ''}`.toLowerCase();
}

function cartHasKind(
  cartIds: Set<string>,
  items: PublicMenuItem[],
  categories: CategoryRef[],
  kind: CategoryKind,
): boolean {
  const catKind = new Map(categories.map((c) => [c.id, effectiveCategoryKind(c)]));
  return items.some((item) => cartIds.has(item.id) && catKind.get(item.category_id) === kind);
}

function cartMissingKinds(
  cartIds: Set<string>,
  items: PublicMenuItem[],
  categories: CategoryRef[],
): CategoryKind[] {
  const kindsInCart = new Set<CategoryKind>();
  const catKind = new Map(categories.map((c) => [c.id, effectiveCategoryKind(c)]));
  for (const item of items) {
    if (!cartIds.has(item.id)) continue;
    const kind = catKind.get(item.category_id);
    if (kind) kindsInCart.add(kind);
  }
  const result: CategoryKind[] = [];
  if (kindsInCart.has('main') && !kindsInCart.has('drink')) result.push('drink');
  if (kindsInCart.has('main') && !kindsInCart.has('side')) result.push('side');
  if (kindsInCart.has('main') && !kindsInCart.has('dessert')) result.push('dessert');
  return result;
}

function ruleMatchesContext(rule: UpsellRule, context: UpsellShowIn): boolean {
  if (rule.show_in === 'both') return true;
  return rule.show_in === context;
}

function ruleMatchesTrigger(
  rule: UpsellRule,
  params: {
    triggerItem?: PublicMenuItem;
    cartIds: Set<string>;
    items: PublicMenuItem[];
    categories: CategoryRef[];
    categoryById: Map<string, CategoryRef>;
  },
): boolean {
  const { triggerItem, cartIds, items, categories, categoryById } = params;

  if (rule.trigger_type === 'item') {
    if (triggerItem && rule.trigger_item_ids.includes(triggerItem.id)) return true;
    return items.some((item) => cartIds.has(item.id) && rule.trigger_item_ids.includes(item.id));
  }

  if (rule.trigger_type === 'category') {
    if (triggerItem && rule.trigger_category_ids.includes(triggerItem.category_id)) return true;
    return items.some(
      (item) => cartIds.has(item.id) && rule.trigger_category_ids.includes(item.category_id),
    );
  }

  if (rule.trigger_type === 'keyword') {
    const keywords = normalizeKeywords(rule.trigger_keywords);
    if (keywords.length === 0) return false;
    const checkItem = (item: PublicMenuItem) => {
      const cat = categoryById.get(item.category_id);
      const hay = haystackForItem(item, cat?.name);
      return keywords.some((kw) => hay.includes(kw));
    };
    if (triggerItem && checkItem(triggerItem)) return true;
    return items.some((item) => cartIds.has(item.id) && checkItem(item));
  }

  if (rule.trigger_type === 'cart_missing_kind') {
    const missing = cartMissingKinds(cartIds, items, categories);
    const wanted = normalizeKeywords(rule.trigger_keywords) as CategoryKind[];
    if (wanted.length > 0) return wanted.some((k) => missing.includes(k));
    const suggestKinds = rule.suggest_kinds ?? [];
    return suggestKinds.some((k) => missing.includes(k));
  }

  return false;
}

function resolveSuggestedItems(
  rule: UpsellRule,
  items: PublicMenuItem[],
  categories: CategoryRef[],
  cartIds: Set<string>,
  limit = 4,
): PublicMenuItem[] {
  const catKind = new Map(categories.map((c) => [c.id, effectiveCategoryKind(c)]));
  const pool: PublicMenuItem[] = [];

  for (const id of rule.suggest_item_ids) {
    const item = items.find((i) => i.id === id && !cartIds.has(i.id));
    if (item) pool.push(item);
  }

  for (const catId of rule.suggest_category_ids) {
    for (const item of items) {
      if (item.category_id === catId && !cartIds.has(item.id)) pool.push(item);
    }
  }

  for (const kind of rule.suggest_kinds) {
    for (const item of items) {
      if (catKind.get(item.category_id) === kind && !cartIds.has(item.id)) pool.push(item);
    }
  }

  const seen = new Set<string>();
  const unique: PublicMenuItem[] = [];
  for (const item of pool) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    unique.push(item);
    if (unique.length >= limit) break;
  }

  if (rule.rule_type === 'upsell' && unique.length > 1) {
    return [unique.sort((a, b) => b.price - a.price)[0]];
  }

  return unique;
}

export function computeUpsellSuggestions(params: {
  rules: UpsellRule[];
  cartItemIds: string[];
  items: PublicMenuItem[];
  categories: CategoryRef[];
  context: UpsellShowIn;
  triggerItem?: PublicMenuItem;
  wizardStep?: WizardStep;
  dismissedRuleIds?: Set<string>;
  limit?: number;
}): UpsellSuggestion[] {
  const {
    rules,
    cartItemIds,
    items,
    categories,
    context,
    triggerItem,
    wizardStep,
    dismissedRuleIds,
    limit = 3,
  } = params;

  const cartIds = new Set(cartItemIds);
  const categoryById = new Map(categories.map((c) => [c.id, c]));
  const activeRules = rules
    .filter((r) => r.is_active && !dismissedRuleIds?.has(r.id))
    .filter((r) => ruleMatchesContext(r, context))
    .sort((a, b) => a.priority - b.priority);

  const suggestions: UpsellSuggestion[] = [];
  const usedItemIds = new Set<string>();

  for (const rule of activeRules) {
    if (wizardStep && wizardStep !== 'details') {
      const stepKinds: CategoryKind[] =
        wizardStep === 'main' ? ['main'] :
        wizardStep === 'side' ? ['side'] :
        wizardStep === 'drink' ? ['drink'] :
        wizardStep === 'dessert' ? ['dessert'] : [];
      if (rule.trigger_type === 'cart_missing_kind' && stepKinds.length > 0) {
        if (!rule.suggest_kinds.some((k) => stepKinds.includes(k))) continue;
      }
    }

    if (!ruleMatchesTrigger(rule, { triggerItem, cartIds, items, categories, categoryById })) {
      continue;
    }

    const suggested = resolveSuggestedItems(rule, items, categories, cartIds, 4).filter(
      (item) => !usedItemIds.has(item.id),
    );
    if (suggested.length === 0) continue;

    for (const item of suggested) usedItemIds.add(item.id);

    suggestions.push({
      rule_id: rule.id,
      rule_name: rule.name,
      rule_type: rule.rule_type,
      message: rule.message?.trim() || defaultMessage(rule),
      items: suggested,
    });

    if (suggestions.length >= limit) break;
  }

  return suggestions;
}

function defaultMessage(rule: UpsellRule): string {
  if (rule.rule_type === 'upsell') return 'Queres experimentar esta opcao premium?';
  return 'Sugestoes para completar o teu pedido:';
}

export function inferCategoryKind(name: string): CategoryKind {
  const n = name.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  if (/entrada|petisco|tapa/.test(n)) return 'starter';
  if (/sopa|caldo|canja/.test(n)) return 'starter';
  if (/acompanh|guarni|batata|migas|feijao/.test(n) && !/menu/.test(n)) return 'side';
  if (/bebida|vinho|cerveja|refri|sumo|\bagua\b|cafe/.test(n)) return 'drink';
  if (/sobremesa|doce|fruta/.test(n)) return 'dessert';
  if (/frango|churrasco|carne|grelhad|peixe|\bave|rodizio|menu|prato|porco|vitela|bifana|prego|secretos|entrecosto|espetada|assado|bacalhau|salm/.test(n)) {
    return 'main';
  }
  if (/combo/.test(n)) return 'combo';
  return 'other';
}

export function effectiveCategoryKind(category: CategoryRef): CategoryKind {
  if (category.kind && category.kind !== 'other') return category.kind;
  return inferCategoryKind(category.name);
}

export function itemsForWizardStep(
  step: WizardStep,
  items: PublicMenuItem[],
  categories: CategoryRef[],
): PublicMenuItem[] {
  if (step === 'details') return [];
  const kind = step as CategoryKind;
  const catIds = new Set(
    categories.filter((c) => effectiveCategoryKind(c) === kind).map((c) => c.id),
  );
  return items.filter((item) => catIds.has(item.category_id));
}

export function parseJsonArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v)).filter(Boolean);
}

export function normalizeUpsellRule(row: Record<string, unknown>): UpsellRule {
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    rule_type: (row.rule_type === 'upsell' ? 'upsell' : 'cross_sell') as UpsellRule['rule_type'],
    trigger_type: (['item', 'category', 'keyword', 'cart_missing_kind'].includes(String(row.trigger_type))
      ? row.trigger_type
      : 'keyword') as UpsellRule['trigger_type'],
    trigger_item_ids: parseJsonArray(row.trigger_item_ids),
    trigger_category_ids: parseJsonArray(row.trigger_category_ids),
    trigger_keywords: parseJsonArray(row.trigger_keywords),
    suggest_item_ids: parseJsonArray(row.suggest_item_ids),
    suggest_category_ids: parseJsonArray(row.suggest_category_ids),
    suggest_kinds: parseJsonArray(row.suggest_kinds) as CategoryKind[],
    message: typeof row.message === 'string' ? row.message : null,
    priority: Number(row.priority ?? 100),
    is_active: row.is_active !== false,
    show_in: (['cart', 'wizard', 'both'].includes(String(row.show_in))
      ? row.show_in
      : 'both') as UpsellRule['show_in'],
  };
}
